/**
 * Belote's UI module.
 *
 * What Belote says that the others do not: play runs anticlockwise, the
 * auction is about one turned card (Classic) or a points-and-suit contract
 * that can be coinched (Coinche), a void player may be *forced* to trump and
 * overtrump, and after the first trick the side with the best declaration lays
 * it face up. Tricks matter for their card points, so the felt counts them.
 */
import type { ReactNode } from 'react'
import {
  beloteRanking,
  belotePoints,
  rankOf,
  suitOf,
  type BeloteContract,
  type BeloteDeclaration,
  type BeloteHandExtra,
  type RankingContext,
  type Seat,
  type Suit,
} from '@cards/shared'
import type { BidLayoutProps, GameUi, LegalCall } from '../types'
import type { TableView } from '../../table/view'
import { Glyph } from '../../table/atoms'
import {
  CardWord, auctionCallsOf, auctionStripOf, handHint, readCalls, seatStatus, sortBySuit, winningSeat,
} from '../common'
import { ClassicBids, CoincheBids, readUpcard } from './BeloteBids'

const TRICKS = 8
const GLYPH: Record<string, Suit> = { '♠': 'S', '♥': 'H', '♦': 'D', '♣': 'C' }
const isRecord = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null

function contractOf(view: TableView): BeloteContract | null {
  const c = view.hand?.contract
  return isRecord(c) && typeof c.taker === 'number' && typeof c.trump === 'string' ? (c as unknown as BeloteContract) : null
}

function extraOf(view: TableView): Partial<BeloteHandExtra> {
  return (view.hand?.extra ?? {}) as Partial<BeloteHandExtra>
}

function variantOf(view: TableView): 'classic' | 'coinche' {
  const a = view.hand?.auction
  if (isRecord(a) && a.variant === 'coinche') return 'coinche'
  const cfg = (view.match?.config ?? view.snapshot.room.config) as { variant?: string } | undefined
  return cfg?.variant === 'coinche' ? 'coinche' : 'classic'
}

const contexts = new WeakMap<object, RankingContext>()
function rankingFor(view: TableView): RankingContext {
  const key = view.hand as object | null
  const hit = key ? contexts.get(key) : undefined
  if (hit) return hit
  const ctx = beloteRanking(contractOf(view)?.trump ?? null)
  if (key) contexts.set(key, ctx)
  return ctx
}

/** `90♦ ×2`, `♥`, `Capot ♠` — the module's own label, drawn with glyphs. */
function LabelText({ label }: { label: string }): JSX.Element {
  const parts = label.split(/([♠♥♦♣])/)
  return (
    <span>
      {parts.map((p, i) => (GLYPH[p] ? <Glyph key={i} suit={GLYPH[p]} /> : <span key={i}>{p}</span>))}
    </span>
  )
}

function callLabel(call: unknown): { label: ReactNode; pass: boolean } {
  if (!isRecord(call)) return { label: '?', pass: false }
  switch (call.type) {
    case 'pass': return { label: 'pass', pass: true }
    case 'take': return { label: <Glyph suit={call.suit as Suit} />, pass: false }
    case 'bid': return { label: <>{String(call.value)}<Glyph suit={call.suit as Suit} /></>, pass: false }
    case 'capot': return { label: <>Capot<Glyph suit={call.suit as Suit} /></>, pass: false }
    case 'coinche': return { label: 'coinche', pass: false }
    case 'surcoinche': return { label: 'surcoinche', pass: false }
    default: return { label: '?', pass: false }
  }
}

const KIND: Record<BeloteDeclaration['kind'], string> = {
  tierce: 'tierce', quarte: 'quarte', quinte: 'quinte', carre: 'carré',
}

function BidLayout(props: BidLayoutProps): JSX.Element {
  return variantOf(props.view) === 'coinche' ? <CoincheBids {...props} /> : <ClassicBids {...props} />
}

export const beloteUi: GameUi = {
  id: 'belote',
  name: 'Belote',
  rotation: 'anticlockwise',
  kittyName: 'Turned card',
  options: [
    {
      key: 'variant',
      label: 'Game',
      choices: [{ label: 'Classic', value: 'classic' }, { label: 'Coinche', value: 'coinche' }],
      resetsOthers: true,
    },
  ],

  tricksPerHand: () => TRICKS,
  tricksNeeded: () => null,

  contractReading: (view) => {
    const hand = view.hand
    if (!hand) return null
    const contract = contractOf(view)
    if (contract) {
      const mine = contract.taker === view.mySeat
      return {
        node: (
          <>
            <LabelText label={contract.label} />
            <span className="who">{mine ? 'You' : view.name(contract.taker)}</span>
          </>
        ),
        caption: contract.bid === null ? 'trump' : 'contract',
      }
    }
    const auction = hand.auction
    if (isRecord(auction) && auction.variant === 'coinche' && isRecord(auction.high)) {
      const high = auction.high as { seat: Seat; value: number | 'capot'; suit: Suit }
      return {
        node: (
          <>
            <span>{high.value === 'capot' ? 'Capot' : high.value}<Glyph suit={high.suit} /></span>
            <span className="who">{high.seat === view.mySeat ? 'You' : view.name(high.seat)}</span>
          </>
        ),
        caption: 'bid to beat',
      }
    }
    const up = readUpcard(auction)
    if (up) {
      const round = isRecord(auction) ? auction.round : 1
      return { node: <CardWord card={up} />, caption: round === 2 ? 'turned, round 2' : 'turned card' }
    }
    return { node: <span className="faint">—</span>, caption: hand.phase === 'abandoned' ? 'passed out' : 'no bid yet' }
  },

  trackCaption: (view) => {
    const hand = view.hand
    if (!hand) return ''
    if (hand.phase === 'abandoned') return 'no tricks played'
    const ctx = contractOf(view)?.trump ?? null
    const team = view.myTeam
    let us = 0
    let them = 0
    for (const t of hand.tricks) {
      if (t.winner === null || !view.match) continue
      const pts = t.plays.reduce((n, p) => n + belotePoints(p.card, ctx), 0) + (t.index === TRICKS - 1 ? 10 : 0)
      if (view.match.table.teamOf[t.winner] === team) us += pts
      else them += pts
    }
    if (us + them === 0) return `${TRICKS} tricks to play`
    return `${us} points, them ${them}`
  },

  seatStatus: (view, seat) => {
    const extra = extraOf(view)
    const parts: ReactNode[] = []
    if (contractOf(view)?.taker === seat) parts.push('taker')
    if (extra.belote?.seat === seat) parts.push(extra.belote.stage)
    const declared = (extra.declarations ?? []).filter((d) => d.seat === seat)
    if (declared.length > 0 && (view.hand?.tricks.length ?? 0) <= 2) parts.push(declared.map((d) => KIND[d.kind]).join(' + '))
    return seatStatus(view, seat, parts)
  },

  winningSeat,

  callout: (view) => {
    const trick = view.trick
    const extra = extraOf(view)
    // Between trick 1 and trick 2 the felt says who scored the declarations.
    if (trick && trick.index === 1 && trick.plays.length === 0 && extra.declarations && extra.declarations.length > 0) {
      const d = extra.declarations
      const total = d.reduce((n, x) => n + x.points, 0)
      return (
        <>
          <b>{view.name(d[0].seat)}</b> declares {d.map((x) => KIND[x.kind]).join(' and ')}, {total} points
        </>
      )
    }
    if (!trick || trick.plays.length === 0) return null
    const leading = trick.winner ?? trick.winning
    const card = trick.plays.find((p) => p.seat === leading)?.card ?? null
    if (leading === null || card === null) return null
    const trump = contractOf(view)?.trump ?? null
    const points = trick.plays.reduce((n, p) => n + belotePoints(p.card, trump), 0)
    const tail = points > 0 ? <>, {points} points</> : null
    return trick.winner !== null
      ? <><b>{view.name(leading)}</b> takes it with <CardWord card={card} />{tail}</>
      : <><b>{view.name(leading)}</b> has it with <CardWord card={card} />{tail}</>
  },

  handHint: (view) => {
    const trick = view.trick
    const trump = contractOf(view)?.trump ?? null
    if (view.isMyTurn && trick && trick.plays.length > 0 && trick.ledSuit && trump) {
      const legal = [...view.legalPlays.keys()]
      const allTrump = legal.length > 0 && legal.every((c) => suitOf(c) === trump)
      const restricted = legal.length < view.myCards.length
      if (restricted && allTrump && trick.ledSuit !== trump) {
        const trumped = trick.plays.some((p) => suitOf(p.card) === trump)
        return (
          <>
            <b>Your turn,</b> you must {trumped ? 'overtrump' : 'trump'} <Glyph suit={trump} />
          </>
        )
      }
      if (restricted && trick.ledSuit === trump && trick.plays.length > 0) {
        return (
          <>
            <b>Your turn,</b> follow <Glyph suit={trump} /> and go over if you can
          </>
        )
      }
    }
    return handHint(view)
  },

  cardMarks: (view, card) => {
    const ctx = rankingFor(view)
    const trump = ctx.isTrump(card)
    const r = rankOf(card)
    const declared = (extraOf(view).declarations ?? []).some((d) => d.cards.includes(card))
    return { trump, stamp: declared ? 'Declared' : trump && (r === 11 || r === 9) ? (r === 11 ? '20' : '14') : null }
  },

  sortHand: (view, cards) => sortBySuit(cards, rankingFor(view)),

  handResult: (view) => {
    const hand = view.hand
    if (hand?.phase === 'abandoned') {
      return { headline: 'Thrown in', sentence: 'Nobody took, so the cards go back and the deal moves on.' }
    }
    const result = hand?.result
    const contract = contractOf(view)
    if (!result || !contract || !view.match) return null
    const team = view.match.table.teamOf[contract.taker]
    const mine = team === view.myTeam
    const other = view.match.table.teams.find((t) => t.id !== team)!.id
    const who = mine ? (contract.taker === view.mySeat ? 'You' : `You and ${view.name(contract.taker)}`) : view.name(contract.taker)
    const capot = hand!.tricks.every((t) => t.winner !== null && view.match!.table.teamOf[t.winner] === view.match!.table.teamOf[hand!.tricks[0].winner!])
      && hand!.tricks.length === TRICKS
    const litige = result.lines.find((l) => l.text.startsWith('Litige:'))
    if (litige) {
      return { headline: 'Litige', sentence: `An exact tie. ${who === 'You' ? 'Your' : `${who}'s`} points wait for whoever wins the next hand.` }
    }
    if (result.contractMade) {
      return {
        headline: capot ? 'Capot' : mine ? 'Made it' : 'They made it',
        sentence: `${who} scored ${result.deltas[team]} to ${result.deltas[other]}.`,
      }
    }
    return {
      headline: mine ? 'Dedans' : 'Set them',
      sentence: `${who} went down, and ${mine ? 'they' : 'you'} take ${result.deltas[other]}.`,
    }
  },

  contractLabelNode: (label) => <LabelText label={label} />,

  auctionRule: 'Play runs to the right. Three passes after a bid end the auction.',

  auctionStrip: (view) => {
    const auction = view.hand?.auction
    const calls = readCalls(auction)
    // Classic runs two rounds: show only the round in progress.
    const round2 = isRecord(auction) && auction.variant === 'classic' && auction.round === 2
    const shown = round2 ? calls.slice(view.seatCount) : calls
    const high = isRecord(auction) && isRecord(auction.high) ? (auction.high.seat as Seat) : null
    return auctionStripOf(view, shown, callLabel, high)
  },

  auctionCalls: (view) => {
    const auction = view.hand?.auction
    const calls = readCalls(auction)
    const round2 = isRecord(auction) && auction.variant === 'classic' && auction.round === 2
    const shown = round2 ? calls.slice(view.seatCount) : calls
    const high = isRecord(auction) && isRecord(auction.high) ? (auction.high.seat as Seat) : null
    return auctionCallsOf(view, shown, callLabel, high)
  },

  isPass: (call: LegalCall) => isRecord(call.call) && call.call.type === 'pass',

  explainHeadline: (call) => <LabelText label={call.label} />,
  bidButtonLabel: (call) => <LabelText label={call.label.startsWith('Take') || call.label.startsWith('Co') || call.label.startsWith('Sur') ? call.label : `Bid ${call.label}`} />,

  BidLayout,

  decisionLayout: () => 'sheet',
  decisionHint: () => null,
  decisionWaiting: () => 'to choose',
}
